import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, ordersApi } from '../api';

function CabinetPage() {
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('client');
    if (!stored) { navigate('/login'); return; }
    setClient(JSON.parse(stored));

    ordersApi.getAll()
      .then(data => setOrders(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    authApi.logout();
    navigate('/login');
  };

  const statusLabel = {
    new: '🆕 Новий',
    processing: '⚙️ Обробляється',
    shipped: '🚛 В дорозі',
    delivered: '✅ Доставлено',
    cancelled: '❌ Скасовано',
  };

  const statusColor = {
    new: { background: '#DBEAFE', color: '#1E40AF' },
    processing: { background: '#FEF3C7', color: '#92400E' },
    shipped: { background: '#E0E7FF', color: '#3730A3' },
    delivered: { background: '#D1FAE5', color: '#065F46' },
    cancelled: { background: '#FEE2E2', color: '#991B1B' },
  };

  const s = {
    page: { minHeight: '100vh', background: '#F8F6F0', padding: '80px 40px 40px' },
    container: { maxWidth: 1000, margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 40 },
    greeting: { fontFamily: 'Georgia, serif', fontSize: 32, fontWeight: 700, color: '#0D1B2A', marginBottom: 4 },
    company: { fontSize: 15, color: '#6B7280' },
    logoutBtn: { background: 'none', border: '1.5px solid #E2DECE', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: '#6B7280' },
    card: { background: '#fff', borderRadius: 12, border: '1px solid #E2DECE', padding: 32, marginBottom: 24 },
    cardTitle: { fontSize: 18, fontWeight: 700, color: '#0D1B2A', marginBottom: 24 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: '#6B7280', borderBottom: '2px solid #E2DECE' },
    td: { padding: '16px', fontSize: 14, color: '#0D1B2A', borderBottom: '1px solid #F1F1EF' },
    badge: (status) => ({ ...statusColor[status] || {background:'#F1F1EF',color:'#6B7280'}, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, display: 'inline-block' }),
    empty: { textAlign: 'center', padding: '48px', color: '#6B7280' },
    infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 },
    infoItem: { padding: '16px', background: '#F8F6F0', borderRadius: 8 },
    infoLabel: { fontSize: 12, color: '#6B7280', marginBottom: 4 },
    infoValue: { fontSize: 15, fontWeight: 600, color: '#0D1B2A' },
  };

  if (!client) return null;

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <div style={s.greeting}>Вітаємо, {client.name}!</div>
            <div style={s.company}>{client.company || 'Особистий акаунт'}</div>
          </div>
          <button style={s.logoutBtn} onClick={logout}>Вийти</button>
        </div>

        {/* Інфо про клієнта */}
        <div style={s.card}>
          <div style={s.cardTitle}>Мої дані</div>
          <div style={s.infoGrid}>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>Ім'я</div>
              <div style={s.infoValue}>{client.name}</div>
            </div>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>Компанія</div>
              <div style={s.infoValue}>{client.company || '—'}</div>
            </div>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>Email</div>
              <div style={s.infoValue}>{client.email}</div>
            </div>
            <div style={s.infoItem}>
              <div style={s.infoLabel}>Статус</div>
              <div style={s.infoValue}>{client.is_approved ? '✅ Підтверджений клієнт' : '⏳ Очікує підтвердження'}</div>
            </div>
          </div>
        </div>

        {/* Замовлення */}
        <div style={s.card}>
          <div style={s.cardTitle}>Мої замовлення</div>
          {loading ? (
            <div style={s.empty}>Завантаження...</div>
          ) : orders.length === 0 ? (
            <div style={s.empty}>
              <div style={{fontSize: 48, marginBottom: 12}}>📦</div>
              <div>Замовлень ще немає</div>
              <a href="/catalog" style={{color: '#C9A84C', marginTop: 8, display: 'inline-block'}}>Перейти до каталогу</a>
            </div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>№</th>
                  <th style={s.th}>Дата</th>
                  <th style={s.th}>Сума</th>
                  <th style={s.th}>Статус</th>
                  <th style={s.th}>Коментар</th>
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td style={s.td}><strong>#{order.id}</strong></td>
                    <td style={s.td}>{new Date(order.created_at).toLocaleDateString('uk-UA')}</td>
                    <td style={s.td}><strong>{Number(order.total).toLocaleString()} ₴</strong></td>
                    <td style={s.td}><span style={s.badge(order.status)}>{statusLabel[order.status] || order.status}</span></td>
                    <td style={s.td}>{order.comment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default CabinetPage;
